import { scoreLabel } from "../core/score.js";
import { metricSeverityClass } from "../analysis/metric-severity.js";
import {
  egtRhythmMetricSeverity,
} from "../analysis/detectors/egt-rhythm.js";

function escapeFindingHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function pdfSafeFindingText(value) {
  return String(value ?? "")
    .replace(/[·•]/g, "-")
    .replace(/[–—]/g, "-")
    .replace(/[°]/g, " deg")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, "");
}

function titleCasePriority(value) {
  return String(value ?? "").replace(/-/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function metricDisplayClass(key, metric) {
  return metricSeverityClass(key, metric);
}

export function findingDisplayClass(finding, metric) {
  if (["cht", "abnormalCombustion"].includes(metric?.key) && metric.details?.severityFloor) return metricDisplayClass(metric.key, metric);
  if (metric?.key === "valve" && egtRhythmMetricSeverity(metric) !== "normal") {
    return metricDisplayClass("valve", metric);
  }
  if (finding?.tone === "cue") return "amber";
  return metricDisplayClass(metric?.key, metric);
}

// Persisted findings can carry status words from before the severity-ladder rename
// (Normal / Watch / Attention); map them at display time so old analysis runs never
// show retired vocabulary next to new surfaces.
const retiredStatusLabels = Object.freeze({
  Good: "Normal",
  Improved: "Normal",
  Investigate: "Attention",
  Monitor: "Watch",
  Review: "Attention",
});

export function findingDisplayLabel(finding, metric) {
  if (metric?.key === "egt" && metric.details?.sustainedEgtSeparation && !metric.details?.hot?.isReview
    && finding?.statusLabel !== "Data review") {
    return findingDisplayClass(finding, metric) === "amber" ? "Watch" : scoreLabel(metric.score);
  }
  if (["cht", "abnormalCombustion"].includes(metric?.key) && metric.details?.severityFloor) return scoreLabel(metric.score);
  const rhythmSeverity = metric?.key === "valve" ? egtRhythmMetricSeverity(metric) : null;
  if (rhythmSeverity === "warning") return "Warning";
  if (rhythmSeverity === "watch") return "Watch";
  if (finding?.statusLabel) return retiredStatusLabels[finding.statusLabel] ?? finding.statusLabel;
  if (finding?.tone === "cue") return "Watch";
  return scoreLabel(metric?.score);
}

export function findingCausePresentation(finding, metric) {
  const className = findingDisplayClass(finding, metric);
  // Numeric percentages read as calculated likelihoods. Most finding cause
  // values are editorial ordering weights, so only render them when a finding
  // explicitly declares that its weights carry evidence-derived semantics.
  const weighted = finding?.weightedCauses === true;
  return {
    title: className === "green" ? "Review Notes" : "Possible Causes",
    weighted,
  };
}

export function findingMetaItems(finding, fallback = "") {
  const explicitItems = (finding?.metaItems ?? [])
    .map((item) => String(item ?? "").trim())
    .filter(Boolean);
  if (explicitItems.length) return explicitItems;

  const rawMeta = String(finding?.meta ?? fallback ?? "").trim();
  if (!rawMeta) return [];

  const inlineItems = rawMeta.split(/\s+·\s+/).map((item) => item.trim()).filter(Boolean);
  if (inlineItems.length <= 1) return [];

  const titleCylinder = String(finding?.title ?? "").match(/\bcylinder\s*#?(\d+)\b/i);
  if (titleCylinder) {
    const cylinderNumber = titleCylinder[1];
    return inlineItems.filter((item) => !new RegExp(`^cyl(?:inder)?\\s*#?${cylinderNumber}$`, "i").test(item));
  }
  return inlineItems;
}

export function findingMetaMarkup(finding, fallback = "") {
  const items = findingMetaItems(finding, fallback);
  if (items.length) {
    return items.map((item) => `<span class="finding-meta-line">${escapeFindingHtml(item)}</span>`).join("");
  }
  return escapeFindingHtml(finding?.meta ?? fallback ?? "");
}

export function reportFindingText(finding, maxLength = 280) {
  const text = String(finding?.body ?? "").replace(/\s+/g, " ").trim();
  if (!text || text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(0, maxLength - 1)).trim()}...`;
}

export function reportEvidenceItems(text) {
  const clean = pdfSafeFindingText(text).replace(/\s+/g, " ").trim();
  if (!clean) return [];
  return clean
    .split(/\s+-\s+|\s+\|\s+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 8);
}

export function reportFindingEvidenceItems(finding, fallbackText) {
  const rows = (finding?.evidence ?? []).filter((item) => item?.label || item?.value || item?.detail);
  if (rows.length) {
    return rows.slice(0, 8).map((item) => {
      const label = String(item.label ?? "Evidence").replace(/\s+probe cue$/i, "");
      if (/all split events/i.test(label) && item.detail) {
        return `${label}: ${item.value ?? ""} (${item.detail})`;
      }
      return item.value ? `${label}: ${item.value}` : `${label}: ${item.detail ?? ""}`;
    });
  }
  return reportEvidenceItems(fallbackText);
}

export function reportFindingEvidenceLine(finding, fallbackText) {
  const metaItems = findingMetaItems(finding, fallbackText);
  if (metaItems.length) return metaItems.join(" | ");
  return finding?.meta ?? fallbackText ?? "";
}

export function reportActionText(priority, action) {
  return `${titleCasePriority(priority)}: ${action}`;
}
